import React, { useState, useEffect } from 'react';
import { Button, message, Spin, Avatar } from 'antd';
import { User, Save, Upload, X } from 'lucide-react';
import Inputs from '../../components/inputs';
import axios from 'axios';
import { API_URL, BUCKET_URL } from '../../config/config';
import { getHeaderOptions } from '../../services/getHeaderOptions';
import { getSignedUrl, uploadToSignedUrl } from '../../services/utilityService';
import './settings.scss';
import logger from '../../utils/logger';

interface UserProfileData {
  _id?: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  phone: string;
  email: string;
  profilePicture?: string;
}

const UserProfileSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [picturePreview, setPicturePreview] = useState<string>('');
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [userData, setUserData] = useState<UserProfileData>({
    firstName: '',
    lastName: '',
    countryCode: '',
    phone: '',
    email: '',
    profilePicture: ''
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      const headers = {
        ...getHeaderOptions(),
        Authorization: localStorage.getItem('auth_token') || ''
      };
      const response = await axios.get(`${API_URL}api/v1/user/profile`, { headers });

      logger.log('[fetchUserProfile] Full API Response:', response.data);
      logger.log('[fetchUserProfile] Response.data keys:', Object.keys(response.data));
      logger.log('[fetchUserProfile] Response.data.data:', response.data.data);
      logger.log('[fetchUserProfile] Response.data.data keys:', Object.keys(response.data.data || {}));

      if (response.data?.data) {
        const userData = response.data.data;
        const profilePicturePath = userData.profile?.filePath || '';
        logger.log('[fetchUserProfile] Profile object:', userData.profile);
        logger.log('[fetchUserProfile] Profile picture path:', profilePicturePath);

        setUserData({
          _id: userData._id || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          countryCode: userData.countryCode || '',
          phone: userData.phone || '',
          email: userData.email || '',
          profilePicture: profilePicturePath
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      message.error('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfilePictureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      message.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error('Image size should be less than 5MB');
      return;
    }

    setPictureFile(file);

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPicturePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadProfilePicture = async (): Promise<string> => {
    if (!pictureFile) return '';

    setUploadingPicture(true);
    try {
      // Get signed URL for user profile picture
      const signedUrlResponse = await getSignedUrl({
        recordId: userData._id || 'temp',
        fileName: pictureFile.name,
        type: 'user',
        mimeType: pictureFile.type
      });

      logger.log('[uploadProfilePicture] Signed URL response:', signedUrlResponse);

      // Upload to S3
      await uploadToSignedUrl(pictureFile, signedUrlResponse.data.signedUrl);

      logger.log('[uploadProfilePicture] Upload successful, filePath:', signedUrlResponse.data.filePath);

      return signedUrlResponse.data.filePath;
    } catch (error) {
      console.error('[uploadProfilePicture] Error:', error);
      message.error('Failed to upload profile picture');
      throw error;
    } finally {
      setUploadingPicture(false);
    }
  };

  const removeProfilePicture = () => {
    setPicturePreview('');
    setPictureFile(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Upload profile picture if new one is selected
      let profilePicturePath = userData.profilePicture;
      if (pictureFile) {
        logger.log('[handleSave] Uploading profile picture...');
        profilePicturePath = await uploadProfilePicture();
        logger.log('[handleSave] Profile picture uploaded:', profilePicturePath);
      }

      const headers = {
        ...getHeaderOptions(),
        Authorization: localStorage.getItem('auth_token') || ''
      };

      // Don't send _id in the update request
      const { _id, ...dataToUpdate } = userData;
      const updatedData = {
        ...dataToUpdate,
        profilePicture: profilePicturePath
      };

      logger.log('[handleSave] Sending update data:', updatedData);

      await axios.put(`${API_URL}api/v1/user/profile`, updatedData, { headers });

      // Update local state
      setUserData(updatedData);
      setPictureFile(null);
      setPicturePreview('');

      message.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating user profile:', error);
      message.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="user-profile-settings">
      <div className="settings-section-header">
        <User size={24} />
        <div>
          <h2>User Profile</h2>
          <p>Manage your personal information</p>
        </div>
      </div>

      <div className="settings-form">
        {/* Profile Picture */}
        <div className="settings-section">
          <h3>Profile Picture</h3>
          <div className="profile-picture-section">
            <Avatar
              size={80}
              src={picturePreview || (userData.profilePicture ? `${BUCKET_URL}${userData.profilePicture}` : undefined)}
              icon={<User />}
              style={{ marginBottom: '1rem' }}
            />
            <div className="profile-picture-actions">
              <input
                type="file"
                id="profile-picture-upload"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleProfilePictureChange}
              />
              <Button
                icon={<Upload size={16} />}
                onClick={() => document.getElementById('profile-picture-upload')?.click()}
                loading={uploadingPicture}
                disabled={uploadingPicture}
              >
                {uploadingPicture ? 'Uploading...' : 'Upload Picture'}
              </Button>
              {(picturePreview || pictureFile) && (
                <Button
                  icon={<X size={16} />}
                  onClick={removeProfilePicture}
                  danger
                >
                  Remove
                </Button>
              )}
            </div>
            <p className="text-muted" style={{ marginTop: '0.5rem' }}>
              Max size: 5MB. Supported: JPG, PNG, GIF
            </p>
          </div>
        </div>

        {/* Personal Information */}
        <div className="settings-section">
          <h3>Personal Information</h3>
          <div className="settings-grid">
            <Inputs
              label="First Name"
              name="firstName"
              type="name"
              value={userData.firstName}
              onChange={handleInputChange}
              placeholder="Enter your first name"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="user-firstname"
            />
            <Inputs
              label="Last Name"
              name="lastName"
              type="name"
              value={userData.lastName}
              onChange={handleInputChange}
              placeholder="Enter your last name"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="user-lastname"
            />
            <Inputs
              label="Email"
              name="email"
              type="email"
              value={userData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              className="form-control"
              autoComplete="off"
              isRequired={true}
              options={[]}
              id="user-email"
              disabled={true}
            />
            <Inputs
              label="Country Code"
              name="countryCode"
              type="text"
              value={userData.countryCode}
              onChange={handleInputChange}
              placeholder="+1"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="user-countrycode"
              disabled={true}
            />
            <Inputs
              label="Phone"
              name="phone"
              type="phone"
              value={userData.phone}
              onChange={handleInputChange}
              placeholder="Enter your phone number"
              className="form-control"
              autoComplete="off"
              isRequired={false}
              options={[]}
              id="user-phone"
              disabled={true}
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="settings-actions">
          <Button
            type="primary"
            icon={<Save size={16} />}
            onClick={handleSave}
            loading={saving}
            size="large"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserProfileSettings;
